import React from "react";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import { usePatientListSelectionModalViewModel } from "./usePatientListSelectionModalViewModel";
import {
	ModalPaper,
	ModalTop,
	ModalTitle,
	CloseButton,
	ModalDescription,
	ModalContent,
	PatientSearch,
	PatientList,
	PatientItem,
	PatientSummary,
	ListAvatar,
	ListPhoto,
	PatientInfo,
	ListName,
	StartButton,
	LoadMoreButton,
	NoResults,
	SkeletonRow,
	LoadMoreSpinnerContainer,
	MiniSpinner,
	ErrorState,
} from "./styles";

import { ReactComponent as PlayCircle } from "../../../../assets/kinologyIcons/svg/play_circle_white.svg";
import { ReactComponent as SearchIcon } from "../../../../assets/kinologyIcons/svg/search.svg";
import { ReactComponent as FallbackIcon } from "../../../../assets/kinologyIcons/svg/patient.svg";
import { ReactComponent as CloseIcon } from "../../../../assets/kinologyIcons/svg/close.svg";
const useStyles = makeStyles(() =>
	createStyles({
		modal: {
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
		},
	})
);

interface PatientListSelectionModalProps {
	isOpen: boolean;
	onClose: () => void;
}

const PatientListSelectionModal: React.FC<PatientListSelectionModalProps> = ({
	isOpen,
	onClose,
}) => {
	const classes = useStyles();
	const {
		searchQuery,
		filteredPatients,
		hasExtraPatients,
		patientsLoading,
		loadingMore,
		error,
		startingPatientId,
		handleSearchChange,
		handleStartEvaluation,
		handleLoadMore,
		retry,
	} = usePatientListSelectionModalViewModel(isOpen, onClose);

	return (
		<Modal
			aria-labelledby="patient-selection-modal-title"
			aria-describedby="patient-selection-modal-description"
			className={classes.modal}
			open={isOpen}
			onClose={onClose}
			closeAfterTransition
			BackdropComponent={Backdrop}
			BackdropProps={{
				timeout: 300,
			}}
		>
			<Fade in={isOpen}>
				<ModalPaper>
					<ModalTop>
						<ModalTitle id="patient-selection-modal-title">
							Lista de Pacientes
						</ModalTitle>
						<CloseButton type="button" onClick={onClose} aria-label="Fechar">
							<CloseIcon />
						</CloseButton>
					</ModalTop>

					<ModalDescription>
						<p id="patient-selection-modal-description">Lista de pacientes</p>
					</ModalDescription>

					<ModalContent>
						<PatientSearch>
							<SearchIcon />
							<input
								type="search"
								value={searchQuery}
								onChange={(e) => handleSearchChange(e.target.value)}
								placeholder="Pesquisar paciente"
								aria-label="Pesquisar paciente"
							/>
						</PatientSearch>

						{patientsLoading && filteredPatients.length === 0 ? (
							<PatientList>
								<SkeletonRow />
								<SkeletonRow />
								<SkeletonRow />
								<SkeletonRow />
							</PatientList>
						) : error && filteredPatients.length === 0 ? (
							<ErrorState role="alert">
								<p>{error}</p>
								<button type="button" onClick={retry}>
									Tentar novamente
								</button>
							</ErrorState>
						) : filteredPatients.length > 0 ? (
							<PatientList>
								{filteredPatients.map((p) => (
									<PatientItem key={p.id}>
										<PatientSummary>
											<ListAvatar hasPhoto={!!p.photoUrl}>
												{p.photoUrl ? (
													<ListPhoto src={p.photoUrl} alt={p.name} />
												) : (
													<FallbackIcon
														width="20px"
														height="20px"
														fill="#ffffff"
													/>
												)}
											</ListAvatar>
											<PatientInfo>
												<ListName>{p.name}</ListName>
											</PatientInfo>
											<StartButton
												type="button"
												onClick={() => handleStartEvaluation(p)}
												disabled={startingPatientId !== null}
											>
												<PlayCircle color="#ffffff" width={40} height={40} />
												{startingPatientId === p.id
													? "Carregando..."
													: "Iniciar"}
											</StartButton>
										</PatientSummary>
									</PatientItem>
								))}
							</PatientList>
						) : (
							<NoResults>Nenhum paciente encontrado.</NoResults>
						)}

						{error && filteredPatients.length > 0 && (
							<ErrorState role="alert">
								<p>{error}</p>
							</ErrorState>
						)}

						{loadingMore ? (
							<LoadMoreSpinnerContainer>
								<MiniSpinner />
							</LoadMoreSpinnerContainer>
						) : hasExtraPatients ? (
							<LoadMoreButton type="button" onClick={handleLoadMore}>
								Carregar mais 8 pacientes
							</LoadMoreButton>
						) : null}
					</ModalContent>
				</ModalPaper>
			</Fade>
		</Modal>
	);
};

export default PatientListSelectionModal;
